import { Dialog, DialogPanel } from '@headlessui/react'
import { useTranslation } from 'react-i18next'
import { IoMdClose } from 'react-icons/io'
import { IoTrashOutline, IoMove, IoImageOutline, IoMusicalNotesOutline, IoAddOutline, IoTextOutline, IoHelpCircleOutline } from 'react-icons/io5'
import { useSelector } from 'react-redux'
import { commonToast } from '../../helper/Toast'
import { ApiDelete, ApiPost, ApiPut } from '../../helper/axiosFunctions'
import { generateRandomId } from '../../helper/commonFunctions'
import { useParams } from 'react-router-dom'
import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import ReactQuill from 'react-quill-new'
import 'react-quill-new/dist/quill.snow.css'
import Loading from '../Loading'
import Button from '../ui/Button'

const MAX_CHARS = 2000

// Part types
const PART_TYPES = {
    TEXT: 'text',
    IMAGE: 'image',
    AUDIO: 'audio',
    INPUT_HINT: 'inputhint'
}

// Answer type options
const ANSWER_TYPE_OPTIONS = [
    { value: 'INPUT', labelKey: 'answerTypeInput' },
    { value: 'MEMORY', labelKey: 'answerTypeMemory' },
    { value: 'TEST', labelKey: 'answerTypeTest' },
]

// Helpers to robustly identify part types coming from API (which may return
// variants like 'text/input', 'text/string', or just 'text').
const isInputHintType = (contentType) =>
    contentType === PART_TYPES.INPUT_HINT || contentType === 'inputhint'
const isTextType = (contentType) =>
    !!contentType && !isInputHintType(contentType) &&
    (contentType === PART_TYPES.TEXT || contentType.includes('text'))
const isImageType = (contentType) =>
    !!contentType && (contentType === PART_TYPES.IMAGE || contentType.includes('image'))
const isAudioType = (contentType) =>
    !!contentType && (contentType === PART_TYPES.AUDIO || contentType.includes('audio'))

const QUILL_MODULES = {
    toolbar: [['bold', 'italic', 'underline']],
}
const QUILL_MODULES_PLAIN = {
    toolbar: false,
}
const QUILL_FORMATS = ['bold', 'italic', 'underline']

/**
 * Learning app only understands <b>, <i>, <u>.
 * Strip Quill's <p>/<br>/<strong>/<em>/etc. and keep just those three tags.
 */
const sanitizeAppRichText = (html) => {
    if (!html || typeof html !== 'string') return ''
    if (html === '<p><br></p>' || html === '<p></p>') return ''

    let s = html
    s = s.replace(/&nbsp;/gi, ' ')
    s = s.replace(/<strong(\s[^>]*)?>/gi, '<b>').replace(/<\/strong>/gi, '</b>')
    s = s.replace(/<em(\s[^>]*)?>/gi, '<i>').replace(/<\/em>/gi, '</i>')
    // Drop attributes on allowed tags: <b class="..."> → <b>
    s = s.replace(/<(b|i|u)(\s[^>]*)?>/gi, '<$1>')
    // Paragraph / line-break structure → plain newlines (not HTML tags)
    s = s.replace(/<br\s*\/?>/gi, '\n')
    s = s.replace(/<\/p>\s*<p[^>]*>/gi, '\n')
    s = s.replace(/<\/?p[^>]*>/gi, '')
    s = s.replace(/<\/?div[^>]*>/gi, '')
    s = s.replace(/<\/?span[^>]*>/gi, '')
    // Remove every tag except b / i / u
    s = s.replace(/<(?!\/?(?:b|i|u)\b)[^>]*>/gi, '')
    s = s.replace(/<(b|i|u)>\s*<\/\1>/gi, '')
    s = s.replace(/\n{3,}/g, '\n\n').trim()
    return s
}

/** WYSIWYG rich text editor (bold / italic / underline). Stores HTML. */
const RichTextEditor = ({
    value = '',
    onChange,
    placeholder,
    autoFocus = false,
    maxLength,
    minHeight = 80,
    variant = 'default',
    showToolbar = true,
}) => {
    const quillRef = useRef(null)

    useEffect(() => {
        if (!autoFocus) return
        const timer = setTimeout(() => {
            const editor = quillRef.current?.getEditor?.()
            editor?.focus()
        }, 50)
        return () => clearTimeout(timer)
    }, [autoFocus])

    const handleChange = (html, _delta, source, editor) => {
        if (source !== 'user') {
            onChange(html === '<p><br></p>' ? '' : html)
            return
        }

        const plain = editor.getText().replace(/\n$/, '')
        if (maxLength != null && plain.length > maxLength) {
            const quill = quillRef.current?.getEditor?.()
            if (quill) {
                quill.deleteText(maxLength, quill.getLength())
            }
            return
        }

        onChange(plain.trim() === '' ? '' : html)
    }

    return (
        <div
            className={`card-rich-editor ${variant === 'hint' ? 'card-rich-editor--hint' : ''} ${showToolbar ? '' : 'card-rich-editor--plain'}`}
            style={{ '--rte-min-height': `${minHeight}px` }}
        >
            <ReactQuill
                ref={quillRef}
                theme="snow"
                value={value || ''}
                onChange={handleChange}
                modules={showToolbar ? QUILL_MODULES : QUILL_MODULES_PLAIN}
                formats={QUILL_FORMATS}
                placeholder={placeholder}
            />
        </div>
    )
}

// Part Renderer Component - defined outside to prevent re-creation
const PartRenderer = ({ part, section, onUpdate, onRemove, autoFocus, t, showToolbar = true }) => {
    if (isTextType(part.contentType)) {
        return (
            <div className="relative group mb-3">
                <RichTextEditor
                    key={showToolbar ? 'formatted' : 'plain'}
                    value={part.contentBody}
                    onChange={(val) => onUpdate(section, part.id, val)}
                    placeholder={t('enterText') || 'Įveskite tekstą...'}
                    autoFocus={autoFocus}
                    maxLength={MAX_CHARS}
                    minHeight={80}
                    showToolbar={showToolbar}
                />
                <button
                    type="button"
                    onClick={() => onRemove(section, part.id)}
                    className="absolute top-1 right-1 z-10 p-1.5 bg-white rounded-lg shadow-sm opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                    title={t('delete') || 'Delete'}
                >
                    <IoTrashOutline className="text-red-500" size={16} />
                </button>
            </div>
        )
    }

    if (isInputHintType(part.contentType)) {
        return (
            <div className="relative group mb-3">
                <div className="flex items-center gap-2 mb-1">
                    <IoHelpCircleOutline className="text-blue-500" size={16} />
                    <span className="text-xs text-blue-600 font-medium">{t('inputHint') || 'Įvesties užuomina'}</span>
                </div>
                <RichTextEditor
                    value={part.contentBody}
                    onChange={(val) => onUpdate(section, part.id, val)}
                    placeholder={t('inputHintPlaceholder') || 'Užuomina kaip atsakyti...'}
                    autoFocus={autoFocus}
                    maxLength={MAX_CHARS}
                    minHeight={60}
                    variant="hint"
                    showToolbar={false}
                />
                <button
                    type="button"
                    onClick={() => onRemove(section, part.id)}
                    className="absolute top-0 right-0 z-10 p-1.5 bg-white rounded-lg shadow-sm opacity-0 group-hover:opacity-100 hover:bg-red-50 transition-all"
                    title={t('delete') || 'Delete'}
                >
                    <IoTrashOutline className="text-red-500" size={16} />
                </button>
            </div>
        )
    }

    if (isImageType(part.contentType)) {
        return (
            <div className="relative group mb-3">
                <img
                    src={part.contentBody}
                    alt="Content"
                    className="w-full h-[150px] object-cover rounded-lg border border-gray-200"
                />
                <div className="absolute top-2 left-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-all">
                    <button
                        type="button"
                        className="p-2 bg-white rounded-lg shadow-md hover:bg-gray-100 transition-all"
                        title={t('move') || 'Move'}
                    >
                        <IoMove className="text-gray-600" size={16} />
                    </button>
                    <button
                        type="button"
                        onClick={() => onRemove(section, part.id)}
                        className="p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-all"
                        title={t('delete') || 'Delete'}
                    >
                        <IoTrashOutline className="text-red-500" size={16} />
                    </button>
                </div>
            </div>
        )
    }

    if (isAudioType(part.contentType)) {
        return (
            <div className="relative group mb-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-3">
                    <IoMusicalNotesOutline className="text-purple-500" size={24} />
                    <audio controls className="flex-1 h-8">
                        <source src={part.contentBody} />
                    </audio>
                    <button
                        type="button"
                        onClick={() => onRemove(section, part.id)}
                        className="p-2 bg-white rounded-lg shadow-sm hover:bg-red-50 transition-all"
                        title={t('delete') || 'Delete'}
                    >
                        <IoTrashOutline className="text-red-500" size={16} />
                    </button>
                </div>
            </div>
        )
    }

    return null
}

// Test Answer Part Renderer for multiple choice answers
const TestAnswerPartRenderer = ({ part, index, onUpdate, onRemove, onToggleCorrect, autoFocus, t }) => {
    const isCorrect = part.contentType === 'test/correct'
    
    return (
        <div className={`relative group mb-3 p-3 rounded-lg border-2 transition-all ${
            isCorrect 
                ? 'border-green-400 bg-green-50' 
                : 'border-gray-200 bg-white hover:border-gray-300'
        }`}>
            <div className="flex items-start gap-3">
                {/* Correct/Wrong indicator */}
                <button
                    type="button"
                    onClick={() => onToggleCorrect(part.id)}
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                        isCorrect 
                            ? 'bg-green-500 text-white' 
                            : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                    }`}
                    title={isCorrect ? (t('markAsWrong') || 'Mark as wrong') : (t('markAsCorrect') || 'Mark as correct')}
                >
                    {isCorrect ? '✓' : String.fromCharCode(65 + index)}
                </button>
                
                {/* Answer text input */}
                <div className="flex-1 min-w-0">
                    <RichTextEditor
                        value={part.contentBody}
                        onChange={(val) => onUpdate(part.id, val)}
                        placeholder={`${t('answerOption') || 'Answer option'} ${index + 1}...`}
                        autoFocus={autoFocus}
                        minHeight={60}
                        showToolbar={false}
                    />
                </div>
                
                {/* Delete button */}
                <button
                    type="button"
                    onClick={() => onRemove(part.id)}
                    className="flex-shrink-0 p-2 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    title={t('delete') || 'Delete'}
                >
                    <IoTrashOutline className="text-red-500" size={18} />
                </button>
            </div>
            
            {/* Label */}
            <div className="mt-2 flex items-center gap-2">
                <span className={`text-xs font-medium ${isCorrect ? 'text-green-600' : 'text-gray-400'}`}>
                    {isCorrect ? (t('correctAnswer') || '✓ Correct answer') : (t('wrongAnswer') || 'Wrong answer')}
                </span>
            </div>
        </div>
    )
}

// Test Answers Column Content Component (without wrapper) for multiple choice
const TestAnswersColumnContent = ({ 
    parts, 
    onAddPart, 
    onUpdatePart, 
    onRemovePart, 
    onToggleCorrect,
    newlyAddedPartId,
    t 
}) => {
    const correctCount = parts.filter(p => p.contentType === 'test/correct').length
    const totalCount = parts.length
    
    return (
        <>
            {/* Header */}
            <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
                <span className="font-medium text-gray-700">{t('answer') || 'Atsakymas'} ({t('multipleChoice') || 'Multiple Choice'})</span>
                <span className="text-sm text-gray-400">
                    {correctCount} {t('correct') || 'correct'} / {totalCount} {t('total') || 'total'}
                </span>
            </div>
            
            {/* Info banner */}
            <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700">
                    {t('testAnswerInfo') || 'Click on the letter/checkmark to mark the correct answer. At least 2 options required, 1 must be correct.'}
                </p>
            </div>

            {/* Parts List */}
            <div className="flex-1 overflow-y-auto space-y-2">
                {parts.map((part, index) => (
                    <TestAnswerPartRenderer
                        key={part.id}
                        part={part}
                        index={index}
                        onUpdate={onUpdatePart}
                        onRemove={onRemovePart}
                        onToggleCorrect={onToggleCorrect}
                        autoFocus={part.id === newlyAddedPartId}
                        t={t}
                    />
                ))}

                {/* Empty state */}
                {parts.length === 0 && (
                    <div 
                        onClick={() => onAddPart()}
                        className="w-full h-[100px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
                    >
                        <div className="text-center text-gray-400">
                            <IoAddOutline size={24} className="mx-auto mb-1" />
                            <span className="text-sm">{t('addAnswerOption') || 'Add answer option'}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom Action Button */}
            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
                <button
                    type="button"
                    onClick={() => onAddPart()}
                    className="flex-1 py-2 px-4 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                    <IoAddOutline className="text-gray-600" size={18} />
                    <span className="text-sm text-gray-600">{t('addAnswerOption') || 'Add answer option'}</span>
                </button>
            </div>
        </>
    )
}

// Answer Column Content Component (without wrapper) for INPUT/MEMORY types
const AnswerColumnContent = ({
    parts,
    imageRef,
    audioRef,
    answerType,
    onAddPart,
    onUpdatePart,
    onRemovePart,
    onFileSelect,
    getCharCount,
    newlyAddedPartId,
    t
}) => {
    const isInput = answerType === 'INPUT'
    const showAnswerToolbar = answerType === 'MEMORY'
    const textPartsCount = parts.filter(p => isTextType(p.contentType)).length

    return (
    <>
        {/* Header */}
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
            <span className="font-medium text-gray-700">{t('answer') || 'Atsakymas'}</span>
            <span className="text-sm text-gray-400">
                {getCharCount(parts)}/{MAX_CHARS} {t('characters') || 'simbolių'}
            </span>
        </div>

        {/* Info banner for INPUT type with alternatives */}
        {isInput && textPartsCount > 1 && (
            <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700">
                    {t('inputAlternativesInfo') || 'Each text field is an alternative correct answer. Any of them will be accepted.'}
                </p>
            </div>
        )}

        {/* Parts List */}
        <div className="flex-1 overflow-y-auto space-y-2">
            {parts.map((part) => (
                <PartRenderer
                    key={part.id}
                    part={part}
                    section="answer"
                    onUpdate={onUpdatePart}
                    onRemove={onRemovePart}
                    autoFocus={part.id === newlyAddedPartId}
                    t={t}
                    showToolbar={showAnswerToolbar}
                />
            ))}

            {/* Empty state - show placeholder to add content */}
            {parts.length === 0 && (
                <div 
                    onClick={() => onAddPart('answer', PART_TYPES.TEXT)}
                    className="w-full h-[100px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
                >
                    <div className="text-center text-gray-400">
                        <IoAddOutline size={24} className="mx-auto mb-1" />
                        <span className="text-sm">{t('clickToAdd') || 'Spustelėkite, kad pridėtumėte'}</span>
                    </div>
                </div>
            )}
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
                type="button"
                onClick={() => onAddPart('answer', PART_TYPES.TEXT)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-1"
                title={isInput ? (t('addAlternativeAnswer') || 'Add alternative answer') : (t('addText') || 'Pridėti tekstą')}
            >
                <IoAddOutline className="text-gray-500" size={18} />
                <IoTextOutline className="text-gray-500" size={16} />
            </button>
            <button
                type="button"
                onClick={() => imageRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                title={t('addImage') || 'Pridėti paveikslėlį'}
            >
                <IoImageOutline className="text-gray-500" size={20} />
            </button>
            <button
                type="button"
                onClick={() => audioRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                title={t('addAudio') || 'Pridėti garsą'}
            >
                <IoMusicalNotesOutline className="text-gray-500" size={20} />
            </button>
        </div>

        {/* Hidden file inputs */}
        <input
            type="file"
            ref={imageRef}
            accept="image/*"
            className="hidden"
            onChange={(e) => onFileSelect(e, 'answer', PART_TYPES.IMAGE)}
        />
        <input
            type="file"
            ref={audioRef}
            accept="audio/*"
            className="hidden"
            onChange={(e) => onFileSelect(e, 'answer', PART_TYPES.AUDIO)}
        />
    </>
    )
}

// Card Column Component - defined outside to prevent re-creation
const CardColumn = ({ 
    title, 
    section, 
    parts, 
    imageRef, 
    audioRef, 
    showInputHint = false,
    onAddPart,
    onUpdatePart,
    onRemovePart,
    onFileSelect,
    getCharCount,
    newlyAddedPartId,
    t
}) => (
    <div className="flex-1 border-r border-gray-200 last:border-r-0 px-4 flex flex-col min-h-[350px]">
        {/* Header */}
        <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-100">
            <span className="font-medium text-gray-700">{title}</span>
            <span className="text-sm text-gray-400">
                {getCharCount(parts)}/{MAX_CHARS} {t('characters') || 'simbolių'}
            </span>
        </div>

        {/* Parts List */}
        <div className="flex-1 overflow-y-auto space-y-2">
            {parts.map((part) => (
                <PartRenderer
                    key={part.id}
                    part={part}
                    section={section}
                    onUpdate={onUpdatePart}
                    onRemove={onRemovePart}
                    autoFocus={part.id === newlyAddedPartId}
                    t={t}
                />
            ))}

            {/* Empty state - show placeholder to add content */}
            {parts.length === 0 && (
                <div 
                    onClick={() => onAddPart(section, PART_TYPES.TEXT)}
                    className="w-full h-[100px] border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/30 transition-all"
                >
                    <div className="text-center text-gray-400">
                        <IoAddOutline size={24} className="mx-auto mb-1" />
                        <span className="text-sm">{t('clickToAdd') || 'Spustelėkite, kad pridėtumėte'}</span>
                    </div>
                </div>
            )}
        </div>

        {/* Bottom Action Buttons */}
        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100">
            <button
                type="button"
                onClick={() => onAddPart(section, PART_TYPES.TEXT)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-1"
                title={t('addText') || 'Pridėti tekstą'}
            >
                <IoAddOutline className="text-gray-500" size={18} />
                <IoTextOutline className="text-gray-500" size={16} />
            </button>
            <button
                type="button"
                onClick={() => imageRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                title={t('addImage') || 'Pridėti paveikslėlį'}
            >
                <IoImageOutline className="text-gray-500" size={20} />
            </button>
            <button
                type="button"
                onClick={() => audioRef.current?.click()}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                title={t('addAudio') || 'Pridėti garsą'}
            >
                <IoMusicalNotesOutline className="text-gray-500" size={20} />
            </button>
            {showInputHint && (
                <button
                    type="button"
                    onClick={() => onAddPart(section, PART_TYPES.INPUT_HINT)}
                    className="p-2 hover:bg-blue-100 rounded-lg transition-all ml-auto"
                    title={t('addInputHint') || 'Pridėti įvesties užuominą'}
                >
                    <IoHelpCircleOutline className="text-blue-500" size={20} />
                </button>
            )}
        </div>

        {/* Hidden file inputs */}
        <input
            type="file"
            ref={imageRef}
            accept="image/*"
            className="hidden"
            onChange={(e) => onFileSelect(e, section, PART_TYPES.IMAGE)}
        />
        <input
            type="file"
            ref={audioRef}
            accept="audio/*"
            className="hidden"
            onChange={(e) => onFileSelect(e, section, PART_TYPES.AUDIO)}
        />
    </div>
)

export default function AddEditQuestionModel({ open, setIsOpen, editMode = false, editingCard = {}, fetchDeckCards, deckDetails = {} }) {
    const { t } = useTranslation()
    const { id: deckId } = useParams()
    const user_data = useSelector((state) => state.profileData.profileData)

    const complexityMap = useMemo(() => {
        if (!deckDetails?.cards) return {};
        const map = {};
        deckDetails.cards.forEach(c => { map[c.cardId] = c.complexity; });
        return map;
    }, [deckDetails]);

    const sortOrderMap = useMemo(() => {
        const map = {};
        (deckDetails?.cards || []).forEach((card) => {
            const sortOrder = Number(card?.sortOrder);
            if (card?.cardId && Number.isFinite(sortOrder)) map[card.cardId] = sortOrder;
        });
        return map;
    }, [deckDetails]);

    const appendedSortOrder = useMemo(() => {
        const values = (deckDetails?.cards || [])
            .map((card) => Number(card?.sortOrder))
            .filter((value) => Number.isFinite(value));
        if (!values.length) return 0;
        return Math.max(...values) + 1;
    }, [deckDetails]);

    const deckLanguage = deckDetails?.language || 'lt'
    const deckCountries = deckDetails?.countries?.length > 0 ? deckDetails.countries : ['LT']
    const deckMainSubject = deckDetails?.mainSubject || 'General'

    // Card metadata state
    const [cardName, setCardName] = useState('')
    const [answerType, setAnswerType] = useState('INPUT')
    const [markText, setMarkText] = useState('')
    const [complexity, setComplexity] = useState(3)
    const [strictCheck, setStrictCheck] = useState(false)

    // Optional AI / TTS override fields (consumed by learning app)
    const [questionTextAI, setQuestionTextAI] = useState('')
    const [answerTextAI, setAnswerTextAI] = useState('')
    const [hintTextAI, setHintTextAI] = useState('')
    const [questionAudio, setQuestionAudio] = useState('')
    const [answerAudio, setAnswerAudio] = useState('')
    const [hintAudio, setHintAudio] = useState('')
    const [showAiAudioSection, setShowAiAudioSection] = useState(false)
    const [uploadingTtsAudio, setUploadingTtsAudio] = useState(null) // 'question' | 'answer' | 'hint' | null

    // Parts state for each section
    const [questionParts, setQuestionParts] = useState([])
    const [answerParts, setAnswerParts] = useState([])
    const [testAnswerParts, setTestAnswerParts] = useState([]) // For TEST type multiple choice
    const [hintParts, setHintParts] = useState([])
    const [showHint, setShowHint] = useState(true)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [newlyAddedPartId, setNewlyAddedPartId] = useState(null)

    useEffect(() => {
        if (newlyAddedPartId) {
            const timer = setTimeout(() => setNewlyAddedPartId(null), 100)
            return () => clearTimeout(timer)
        }
    }, [newlyAddedPartId])

    // File input refs
    const questionImageRef = useRef(null)
    const questionAudioRef = useRef(null)
    const answerImageRef = useRef(null)
    const answerAudioRef = useRef(null)
    const hintImageRef = useRef(null)
    const hintAudioRef = useRef(null)
    const ttsQuestionAudioRef = useRef(null)
    const ttsAnswerAudioRef = useRef(null)
    const ttsHintAudioRef = useRef(null)

    const resetAiAudioFields = useCallback(() => {
        setQuestionTextAI('')
        setAnswerTextAI('')
        setHintTextAI('')
        setQuestionAudio('')
        setAnswerAudio('')
        setHintAudio('')
        setStrictCheck(false)
        setShowAiAudioSection(false)
        setUploadingTtsAudio(null)
    }, [])
    // Create a new text part
    const createTextPart = useCallback((prefix) => ({
        id: `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        contentType: 'text/input',
        contentBody: '',
        isExisting: false
    }), [])

    // Create a new part of specified type
    const createPart = useCallback((prefix, type, content = '') => {
        // Map internal types to API content types
        const contentTypeMap = {
            [PART_TYPES.TEXT]: 'text/input',
            [PART_TYPES.IMAGE]: 'image',
            [PART_TYPES.AUDIO]: 'audio',
            [PART_TYPES.INPUT_HINT]: 'inputhint'
        }
        
        return {
            id: `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            contentType: contentTypeMap[type] || type,
            contentBody: content,
            isExisting: false
        }
    }, [])

    // Create a new test answer part (for multiple choice)
    const createTestAnswerPart = useCallback((isCorrect = false, content = '') => {
        return {
            id: `test-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            contentType: isCorrect ? 'test/correct' : 'test/wrong',
            contentBody: content,
            isExisting: false
        }
    }, [])

    // Initialize parts when modal opens or editingCard changes
    useEffect(() => {
        if (open) {
            if (editMode && editingCard) {
                // Set card metadata
                setCardName(editingCard.cardName || '')
                setAnswerType(editingCard.answerType || 'INPUT')
                setMarkText(editingCard.mark?.text || '')
                setComplexity(complexityMap[editingCard.cardId] ?? editingCard.complexity ?? 3)
                setStrictCheck(Boolean(editingCard.strictCheck))
                setQuestionTextAI(editingCard.questionTextAI || '')
                setAnswerTextAI(editingCard.answerTextAI || '')
                setHintTextAI(editingCard.hintTextAI || '')
                setQuestionAudio(editingCard.questionAudio || '')
                setAnswerAudio(editingCard.answerAudio || '')
                setHintAudio(editingCard.hintAudio || '')
                setShowAiAudioSection(Boolean(
                    editingCard.questionTextAI ||
                    editingCard.answerTextAI ||
                    editingCard.hintTextAI ||
                    editingCard.questionAudio ||
                    editingCard.answerAudio ||
                    editingCard.hintAudio ||
                    editingCard.strictCheck
                ))

                // Parse question parts
                const qParts = editingCard.question?.parts?.map((part, idx) => ({
                    id: `q-existing-${idx}-${Math.random().toString(36).substr(2, 9)}`,
                    contentType: part.contentType,
                    contentBody: part.contentBody,
                    isExisting: true
                })) || []
                
                // Parse hint parts (first hint)
                const hParts = editingCard.hints?.[0]?.parts?.map((part, idx) => ({
                    id: `h-existing-${idx}-${Math.random().toString(36).substr(2, 9)}`,
                    contentType: part.contentType,
                    contentBody: part.contentBody,
                    isExisting: true
                })) || []

                // Check if it's a TEST type card
                if (editingCard.answerType === 'TEST') {
                    // For TEST type, parse all answer parts from all answers
                    const testParts = editingCard.answers?.flatMap((answer, ansIdx) => 
                        answer.parts?.map((part, idx) => ({
                            id: `test-existing-${ansIdx}-${idx}-${Math.random().toString(36).substr(2, 9)}`,
                            contentType: part.contentType, // should be test/correct or test/wrong
                            contentBody: part.contentBody,
                            isExisting: true
                        })) || []
                    ) || []
                    
                    setTestAnswerParts(testParts.length > 0 ? testParts : [
                        createTestAnswerPart(true, ''),
                        createTestAnswerPart(false, '')
                    ])
                    setAnswerParts([createTextPart('a')]) // Keep empty for non-test
                } else if (editingCard.answerType === 'INPUT') {
                    // For INPUT type, multiple `answers` entries represent
                    // alternative correct answers (e.g. "USA" / "United States").
                    // Flatten all text parts across answers (each text part = one
                    // alternative shown in the editor) and preserve any media
                    // parts (image/audio) from any answer.
                    const allParts = (editingCard.answers || []).flatMap(a => a?.parts || [])
                    const textParts = allParts.filter(p => isTextType(p.contentType))
                    const mediaParts = allParts.filter(p => isImageType(p.contentType) || isAudioType(p.contentType))

                    const mappedTextParts = textParts.map((part, idx) => ({
                        id: `a-existing-text-${idx}-${Math.random().toString(36).substr(2, 9)}`,
                        contentType: 'text/input',
                        contentBody: part.contentBody || '',
                        isExisting: true
                    }))
                    const mappedMediaParts = mediaParts.map((part, idx) => ({
                        id: `a-existing-media-${idx}-${Math.random().toString(36).substr(2, 9)}`,
                        contentType: part.contentType,
                        contentBody: part.contentBody,
                        isExisting: true
                    }))

                    const aParts = [...mappedTextParts, ...mappedMediaParts]
                    setAnswerParts(aParts.length > 0 ? aParts : [createTextPart('a')])
                    setTestAnswerParts([createTestAnswerPart(true, ''), createTestAnswerPart(false, '')])
                } else {
                    // Parse answer parts (first answer) for MEMORY
                    const aParts = editingCard.answers?.[0]?.parts?.map((part, idx) => ({
                        id: `a-existing-${idx}-${Math.random().toString(36).substr(2, 9)}`,
                        contentType: part.contentType,
                        contentBody: part.contentBody,
                        isExisting: true
                    })) || []

                    setAnswerParts(aParts.length > 0 ? aParts : [createTextPart('a')])
                    setTestAnswerParts([createTestAnswerPart(true, ''), createTestAnswerPart(false, '')])
                }

                setQuestionParts(qParts.length > 0 ? qParts : [createTextPart('q')])
                setHintParts(hParts.length > 0 ? hParts : [createTextPart('h')])
                setShowHint(hParts.length > 0 || true)
            } else {
                // Reset card metadata
                setCardName('')
                setAnswerType('INPUT')
                setMarkText('')
                setComplexity(3)
                resetAiAudioFields()
                
                // Initialize with empty text parts
                setQuestionParts([createTextPart('q')])
                setAnswerParts([createTextPart('a')])
                setTestAnswerParts([createTestAnswerPart(true, ''), createTestAnswerPart(false, '')])
                setHintParts([createTextPart('h')])
                setShowHint(true)
            }
        }
    }, [open, editMode, editingCard, createTextPart, createTestAnswerPart, complexityMap, resetAiAudioFields])

    const handleClose = () => setIsOpen(false)

    // Add a new part to a section
    const addPart = useCallback((section, type, content = '') => {
        const prefix = section === 'question' ? 'q' : section === 'answer' ? 'a' : 'h'
        const newPart = createPart(prefix, type, content)
        
        if (section === 'question') {
            setQuestionParts(prev => [...prev, newPart])
        } else if (section === 'answer') {
            setAnswerParts(prev => [...prev, newPart])
        } else {
            setHintParts(prev => [...prev, newPart])
        }
        setNewlyAddedPartId(newPart.id)
    }, [createPart])

    // Update a part's content
    const updatePart = useCallback((section, partId, newContent) => {
        const updateFn = (parts) => parts.map(p => 
            p.id === partId ? { ...p, contentBody: newContent } : p
        )
        
        if (section === 'question') {
            setQuestionParts(updateFn)
        } else if (section === 'answer') {
            setAnswerParts(updateFn)
        } else {
            setHintParts(updateFn)
        }
    }, [])

    // Remove a part
    const removePart = useCallback((section, partId) => {
        const filterFn = (parts) => parts.filter(p => p.id !== partId)
        
        if (section === 'question') {
            setQuestionParts(filterFn)
        } else if (section === 'answer') {
            setAnswerParts(filterFn)
        } else {
            setHintParts(filterFn)
        }
    }, [])

    // Add a test answer option
    const addTestAnswerPart = useCallback(() => {
        const newPart = createTestAnswerPart(false, '')
        setTestAnswerParts(prev => [...prev, newPart])
        setNewlyAddedPartId(newPart.id)
    }, [createTestAnswerPart])

    // Update a test answer part's content
    const updateTestAnswerPart = useCallback((partId, newContent) => {
        setTestAnswerParts(prev => prev.map(p => 
            p.id === partId ? { ...p, contentBody: newContent } : p
        ))
    }, [])

    // Remove a test answer part
    const removeTestAnswerPart = useCallback((partId) => {
        setTestAnswerParts(prev => prev.filter(p => p.id !== partId))
    }, [])

    // Toggle test answer correct/wrong status
    const toggleTestAnswerCorrect = useCallback((partId) => {
        setTestAnswerParts(prev => prev.map(p => {
            if (p.id === partId) {
                return {
                    ...p,
                    contentType: p.contentType === 'test/correct' ? 'test/wrong' : 'test/correct'
                }
            }
            return p
        }))
    }, [])

    // Upload a single file to the server immediately
    const uploadFileToServer = useCallback(async (file) => {
        const folderOrDeckId = deckDetails?.folder || deckId
        if (!folderOrDeckId) {
            throw new Error('Deck ID or folder is required for file upload')
        }

        const formData = new FormData()
        formData.append("deckId", folderOrDeckId)
        formData.append("file1", file)

        const response = await ApiPost('/contents/images-upload/deck', formData)
        const uploadedUrls = response?.data || []
        if (uploadedUrls[0]) {
            return uploadedUrls[0]
        }
        throw new Error('No URL returned from upload')
    }, [deckDetails, deckId])

    // Handle file selection — uploads immediately, stores real URL
    const handleFileSelect = useCallback(async (e, section, type) => {
        const file = e.target.files?.[0]
        if (!file) return
        e.target.value = null

        const partId = `${section === 'question' ? 'q' : section === 'answer' ? 'a' : 'h'}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        const previewUrl = URL.createObjectURL(file)

        const newPart = {
            id: partId,
            contentType: type === PART_TYPES.IMAGE ? 'image' : 'audio',
            contentBody: previewUrl,
            isExisting: false,
            isUploading: true
        }

        const setParts = section === 'question' ? setQuestionParts
            : section === 'answer' ? setAnswerParts
            : setHintParts

        setParts(prev => [...prev, newPart])

        try {
            const uploadedUrl = await uploadFileToServer(file)
            URL.revokeObjectURL(previewUrl)
            setParts(prev => prev.map(p =>
                p.id === partId
                    ? { ...p, contentBody: uploadedUrl, isUploading: false }
                    : p
            ))
        } catch (error) {
            commonToast(error?.data?.errorDescription || error?.data?.error || error?.message || 'Failed to upload file', 'error')
            setParts(prev => prev.filter(p => p.id !== partId))
            URL.revokeObjectURL(previewUrl)
        }
    }, [uploadFileToServer])

    // Get total character count for a section's text parts
    const getCharCount = useCallback((parts) => {
        return parts
            .filter(p => isTextType(p.contentType) || isInputHintType(p.contentType))
            .reduce((sum, p) => {
                const plain = (p.contentBody || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ')
                return sum + plain.length
            }, 0)
    }, [])

    // Format parts for API submission (excludes parts still uploading).
    // Text/hint bodies are sanitized to <b>/<i>/<u> only — no <p> or other tags.
    const formatPartsForApi = (parts) => {
        return parts
            .filter(p => p.contentBody && p.contentBody.trim() !== '' && !p.isUploading)
            .map(p => {
                const isRichText =
                    isTextType(p.contentType) ||
                    isInputHintType(p.contentType) ||
                    p.contentType === 'test/correct' ||
                    p.contentType === 'test/wrong'
                return {
                    contentType: p.contentType,
                    contentBody: isRichText
                        ? sanitizeAppRichText(p.contentBody)
                        : p.contentBody,
                }
            })
            .filter(p => p.contentBody && String(p.contentBody).trim() !== '')
    }

    const handleSubmit = async () => {
        // Validate - at least one text part in question
        const hasQuestionText = questionParts.some(p =>
            isTextType(p.contentType) && p.contentBody.trim()
        )

        if (!hasQuestionText) {
            commonToast(t('validation.questionrequired') || 'Question is required', 'error')
            return
        }

        // Validate based on answer type
        if (answerType === 'TEST') {
            // For TEST type, need at least 2 options with content
            const validTestParts = testAnswerParts.filter(p => p.contentBody.trim())
            if (validTestParts.length < 2) {
                commonToast(t('validation.testMinimumOptions') || 'At least 2 answer options are required for multiple choice', 'error')
                return
            }
            // Need at least one correct answer
            const hasCorrect = testAnswerParts.some(p => p.contentType === 'test/correct' && p.contentBody.trim())
            if (!hasCorrect) {
                commonToast(t('validation.testNeedsCorrect') || 'Please mark at least one answer as correct', 'error')
                return
            }
        } else {
            // For INPUT/MEMORY, validate regular answer
            const hasAnswerText = answerParts.some(p =>
                isTextType(p.contentType) && p.contentBody.trim()
            )
            if (!hasAnswerText) {
                commonToast(t('validation.answerrequired') || 'Answer is required', 'error')
                return
            }
        }

        setIsSubmitting(true)

        try {
            const questionPartsFormatted = formatPartsForApi(questionParts)
            
            // Build answers array based on answer type
            let answers = []
            
            if (answerType === 'TEST') {
                answers = testAnswerParts
                    .filter(p => p.contentBody.trim())
                    .map((part, index) => ({
                        order: index + 1,
                        parts: [{
                            contentType: part.contentType,
                            contentBody: sanitizeAppRichText(part.contentBody)
                        }],
                        score: part.contentType === 'test/correct' ? 5 : 0,
                        language: deckLanguage
                    }))
                    .filter((answer) => answer.parts[0]?.contentBody)
            } else if (answerType === 'INPUT') {
                // For INPUT type, each text part is treated as an alternative
                // correct answer and stored as its own entry in `answers[]`.
                // Media parts (image/audio) are attached to the first answer.
                const formatted = formatPartsForApi(answerParts)
                const textParts = formatted.filter(p => isTextType(p.contentType))
                const mediaParts = formatted.filter(p => isImageType(p.contentType) || isAudioType(p.contentType))

                answers = textParts.map((part, index) => ({
                    order: index + 1,
                    parts: index === 0 ? [part, ...mediaParts] : [part],
                    score: 5,
                    language: deckLanguage
                }))

                if (answers.length === 0 && mediaParts.length > 0) {
                    answers = [{
                        order: 1,
                        parts: mediaParts,
                        score: 5,
                        language: deckLanguage
                    }]
                }
            } else {
                const answerPartsFormatted = formatPartsForApi(answerParts)
                answers = [{
                    order: 1,
                    parts: answerPartsFormatted,
                    score: 5,
                    language: deckLanguage
                }]
            }

            // Build hints array
            const hints = []
            if (showHint) {
                const hintPartsFormatted = formatPartsForApi(hintParts)
                if (hintPartsFormatted.length > 0) {
                    hints.push({ parts: hintPartsFormatted })
                }
            }

            const cardId = editMode ? editingCard.cardId : generateRandomId()

            // Card Name field was removed from the UI but the backend still
            // requires a non-empty value. Preserve any existing name when
            // editing; otherwise auto-generate a unique placeholder name.
            const resolvedCardName = (cardName && cardName.trim())
                || (editMode && editingCard?.cardName)
                || `card-${cardId}`

            const payload = {
                cardId: cardId,
                cardName: resolvedCardName,
                shortDescription: resolvedCardName,
                longDescription: resolvedCardName,
                status: "WORK_IN_PROGRESS",
                mainSubject: deckMainSubject,
                subArgument: "General",
                language: deckLanguage,
                countries: deckCountries,
                score: 45,
                schoolTypes: ["PUBLIC"],
                schoolLevels: [],
                otherCardContents: [],
                question: { parts: questionPartsFormatted },
                answerType: answerType,
                answers: answers,
                hints: hints,
                mark: {
                    type: "",
                    text: markText.trim(),
                    color: "FFA31A,FFF7E6",
                    iconImage: "Star"
                },
                strictCheck,
                questionTextAI: questionTextAI.trim() || null,
                answerTextAI: answerTextAI.trim() || null,
                hintTextAI: hintTextAI.trim() || null,
                questionAudio: questionAudio.trim() || null,
                answerAudio: answerAudio.trim() || null,
                hintAudio: hintAudio.trim() || null,
            }

            // Complexity lives on the deck↔card link (`/contents/decks/cards`),
            // not on the card document itself.
            const resolvedDeckId = deckId || deckDetails?.deckId
            const resolvedComplexity = Number(complexity)

            if (!resolvedDeckId) {
                throw new Error(t('deckIdRequired') || 'Deck ID is required to save complexity')
            }

            if (editMode) {
                await ApiPut(`/contents/cards`, payload)
                const currentComplexity = Number(complexityMap[editingCard.cardId])
                if (currentComplexity !== resolvedComplexity) {
                    await ApiDelete(`/contents/decks/${resolvedDeckId}/cards/${editingCard.cardId}`)
                    await ApiPost('/contents/decks/cards', {
                        deckId: resolvedDeckId,
                        card: {
                            cardId: editingCard.cardId,
                            complexity: resolvedComplexity,
                            sortOrder: Number.isFinite(sortOrderMap[editingCard.cardId])
                                ? sortOrderMap[editingCard.cardId]
                                : 0,
                        },
                    })
                }
                commonToast(t('deckUpdatedSuccess'), 'success')
            } else {
                await ApiPost('/contents/cards', payload)
                await ApiPost('/contents/decks/cards', {
                    deckId: resolvedDeckId,
                    card: {
                        cardId: cardId,
                        complexity: resolvedComplexity,
                        sortOrder: appendedSortOrder,
                    },
                })
                commonToast(t('deckCardAddedSuccess'), 'success')
            }
            
            setIsOpen(false)
            await fetchDeckCards?.()
        } catch (error) {
            commonToast(error?.data?.errorDescription || error?.data?.error || error?.message, 'error')
        } finally {
            setIsSubmitting(false)
        }
    }

    const stripHtmlToPlain = useCallback((html) => {
        return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
    }, [])

    const getFirstTextFromParts = useCallback((parts) => {
        const textPart = (parts || []).find(
            (p) => (isTextType(p.contentType) || isInputHintType(p.contentType)) && p.contentBody
        )
        return stripHtmlToPlain(textPart?.contentBody)
    }, [stripHtmlToPlain])

    const fillTextAIFromParts = useCallback((section) => {
        if (section === 'question') {
            setQuestionTextAI(getFirstTextFromParts(questionParts))
        } else if (section === 'answer') {
            if (answerType === 'TEST') {
                setAnswerTextAI(
                    testAnswerParts
                        .filter((p) => p.contentBody?.trim())
                        .map((p) => stripHtmlToPlain(p.contentBody))
                        .filter(Boolean)
                        .join('. ')
                )
            } else {
                setAnswerTextAI(getFirstTextFromParts(answerParts))
            }
        } else {
            setHintTextAI(getFirstTextFromParts(hintParts))
        }
    }, [answerParts, answerType, getFirstTextFromParts, hintParts, questionParts, stripHtmlToPlain, testAnswerParts])

    const handleTtsAudioUpload = useCallback(async (e, section) => {
        const file = e.target.files?.[0]
        if (!file) return
        e.target.value = null

        setUploadingTtsAudio(section)
        try {
            const uploadedUrl = await uploadFileToServer(file)
            if (section === 'question') setQuestionAudio(uploadedUrl)
            else if (section === 'answer') setAnswerAudio(uploadedUrl)
            else setHintAudio(uploadedUrl)
        } catch (error) {
            commonToast(
                error?.data?.errorDescription || error?.data?.error || error?.message || 'Failed to upload file',
                'error'
            )
        } finally {
            setUploadingTtsAudio(null)
        }
    }, [uploadFileToServer])

    const handleClearAll = () => {
        setCardName('')
        setAnswerType('INPUT')
        setMarkText('')
        setComplexity(3)
        resetAiAudioFields()
        setQuestionParts([createTextPart('q')])
        setAnswerParts([createTextPart('a')])
        setTestAnswerParts([createTestAnswerPart(true, ''), createTestAnswerPart(false, '')])
        setHintParts([createTextPart('h')])
    }

    return (
        <Dialog
            open={open}
            as="div"
            className="relative z-30 focus:outline-none"
            onClose={() => {}}
        >
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 bg-black/35 backdrop-blur-sm">
                    <DialogPanel
                        transition
                        className="w-full relative max-w-5xl rounded-2xl bg-white duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0 shadow-2xl"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <span className="w-7 h-7 bg-gray-800 text-white rounded-full flex items-center justify-center text-sm font-medium">
                                        1
                                    </span>
                                    <span className="font-semibold text-gray-800">
                                        {editMode ? t("editCard") : t("newQuestion") || "Naujas klausimas"}
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    onClick={handleClearAll}
                                    className="flex items-center gap-2 px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                                >
                                    <IoTrashOutline size={18} />
                                    <span className="text-sm">{t('Delete')}</span>
                                </button>
                            </div>

                            <div className="flex items-center gap-4">
                                {/* Add Hint Checkbox */}
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={showHint}
                                        onChange={(e) => setShowHint(e.target.checked)}
                                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm text-gray-600">{t('addHint') || 'Pridėti suflerį'}</span>
                                </label>

                                {/* Close Button */}
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-all"
                                >
                                    <IoMdClose size={24} className="text-gray-500" />
                                </button>
                            </div>
                        </div>

                        {/* Card Metadata Section */}
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 space-y-4">
                            {/* Row 1: Complexity, Answer Type, Mark */}
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('complexity')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={complexity}
                                        onChange={(e) => setComplexity(Number(e.target.value))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-white"
                                    >
                                        <option value={1}>{t('complexityVeryEasy')}</option>
                                        <option value={2}>{t('complexityEasy')}</option>
                                        <option value={3}>{t('complexityMedium')}</option>
                                        <option value={4}>{t('complexityHard')}</option>
                                        <option value={5}>{t('complexityVeryHard')}</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('answerType')} <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        value={answerType}
                                        onChange={(e) => setAnswerType(e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-white"
                                    >
                                        {ANSWER_TYPE_OPTIONS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {t(option.labelKey)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {t('mark')}
                                    </label>
                                    <input
                                        type="text"
                                        value={markText}
                                        onChange={(e) => setMarkText(e.target.value)}
                                        placeholder={t('markPlaceholder')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">{t('markHelperText')}</p>
                                </div>
                            </div>
                        </div>

                        {/* Card Columns */}
                        <div className="flex p-4">
                            {/* Question Column - with inputhint option */}
                            <CardColumn
                                title={t('question') || 'Klausimas'}
                                section="question"
                                parts={questionParts}
                                imageRef={questionImageRef}
                                audioRef={questionAudioRef}
                                showInputHint={answerType === 'INPUT'}
                                onAddPart={addPart}
                                onUpdatePart={updatePart}
                                onRemovePart={removePart}
                                onFileSelect={handleFileSelect}
                                getCharCount={getCharCount}
                                newlyAddedPartId={newlyAddedPartId}
                                t={t}
                            />

                            {/* Answer Column */}
                            <div className="flex-1 border-r border-gray-200 last:border-r-0 px-4 flex flex-col min-h-[350px]">
                                <h3 className="font-semibold text-gray-800 mb-3">{t('answer')}</h3>
                                {answerType === 'TEST' ? (
                                    <TestAnswersColumnContent
                                        parts={testAnswerParts}
                                        onAddPart={addTestAnswerPart}
                                        onUpdatePart={updateTestAnswerPart}
                                        onRemovePart={removeTestAnswerPart}
                                        onToggleCorrect={toggleTestAnswerCorrect}
                                        newlyAddedPartId={newlyAddedPartId}
                                        t={t}
                                    />
                                ) : (
                                    <AnswerColumnContent
                                        parts={answerParts}
                                        imageRef={answerImageRef}
                                        audioRef={answerAudioRef}
                                        answerType={answerType}
                                        onAddPart={addPart}
                                        onUpdatePart={updatePart}
                                        onRemovePart={removePart}
                                        onFileSelect={handleFileSelect}
                                        getCharCount={getCharCount}
                                        newlyAddedPartId={newlyAddedPartId}
                                        t={t}
                                    />
                                )}
                            </div>

                            {/* Hint Column (conditional) */}
                            {showHint && (
                                <CardColumn
                                    title={t('Prompter') || 'Sufleris'}
                                    section="hint"
                                    parts={hintParts}
                                    imageRef={hintImageRef}
                                    audioRef={hintAudioRef}
                                    onAddPart={addPart}
                                    onUpdatePart={updatePart}
                                    onRemovePart={removePart}
                                    onFileSelect={handleFileSelect}
                                    getCharCount={getCharCount}
                                    newlyAddedPartId={newlyAddedPartId}
                                    t={t}
                                />
                            )}
                        </div>

                        {/* AI / Audio overrides (optional) */}
                        <div className="px-6 py-3 border-t border-gray-200">
                            <button
                                type="button"
                                onClick={() => setShowAiAudioSection((prev) => !prev)}
                                className="flex items-center justify-between w-full text-left"
                            >
                                <span className="text-sm font-semibold text-gray-800">
                                    {t('aiAudioSection') || 'AI / Audio (optional)'}
                                </span>
                                <span className="text-xs text-gray-500">
                                    {showAiAudioSection ? '▲' : '▼'}
                                </span>
                            </button>

                            {showAiAudioSection && (
                                <div className="mt-3 space-y-4">
                                    <p className="text-xs text-gray-500">
                                        {t('aiAudioHelper') ||
                                            'Playback priority: audio URL → TTS from AI text → text from card parts.'}
                                    </p>

                                    {[
                                        {
                                            key: 'question',
                                            title: t('question') || 'Question',
                                            textValue: questionTextAI,
                                            setText: setQuestionTextAI,
                                            audioValue: questionAudio,
                                            setAudio: setQuestionAudio,
                                            audioRef: ttsQuestionAudioRef,
                                        },
                                        {
                                            key: 'answer',
                                            title: t('answer') || 'Answer',
                                            textValue: answerTextAI,
                                            setText: setAnswerTextAI,
                                            audioValue: answerAudio,
                                            setAudio: setAnswerAudio,
                                            audioRef: ttsAnswerAudioRef,
                                        },
                                        {
                                            key: 'hint',
                                            title: t('Prompter') || 'Hint',
                                            textValue: hintTextAI,
                                            setText: setHintTextAI,
                                            audioValue: hintAudio,
                                            setAudio: setHintAudio,
                                            audioRef: ttsHintAudioRef,
                                        },
                                    ].map((section) => (
                                        <div
                                            key={section.key}
                                            className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2"
                                        >
                                            <div className="flex items-center justify-between gap-2">
                                                <h4 className="text-sm font-medium text-gray-700">
                                                    {section.title}
                                                </h4>
                                                <button
                                                    type="button"
                                                    onClick={() => fillTextAIFromParts(section.key)}
                                                    className="text-xs text-blue-600 hover:underline"
                                                >
                                                    {t('fillFromText') || 'Fill from text'}
                                                </button>
                                            </div>

                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">
                                                    {t('ttsTextOptional') || 'TTS text (optional)'}
                                                </label>
                                                <textarea
                                                    value={section.textValue}
                                                    onChange={(e) => section.setText(e.target.value)}
                                                    rows={2}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-white resize-none"
                                                    placeholder={t('ttsTextPlaceholder') || 'Text used for TTS if no audio URL'}
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">
                                                    {t('audioUrlOptional') || 'Audio URL (optional)'}
                                                </label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        value={section.audioValue}
                                                        onChange={(e) => section.setAudio(e.target.value)}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-white"
                                                        placeholder="https://..."
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => section.audioRef.current?.click()}
                                                        disabled={uploadingTtsAudio === section.key}
                                                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-white disabled:opacity-50"
                                                    >
                                                        {uploadingTtsAudio === section.key
                                                            ? (t('uploading') || 'Uploading...')
                                                            : (t('uploadAudio') || 'Upload')}
                                                    </button>
                                                    <input
                                                        type="file"
                                                        ref={section.audioRef}
                                                        accept="audio/*"
                                                        className="hidden"
                                                        onChange={(e) => handleTtsAudioUpload(e, section.key)}
                                                    />
                                                </div>
                                                {section.audioValue ? (
                                                    <audio
                                                        controls
                                                        className="w-full h-8 mt-2"
                                                        src={section.audioValue}
                                                    />
                                                ) : null}
                                            </div>
                                        </div>
                                    ))}

                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={strictCheck}
                                            onChange={(e) => setStrictCheck(e.target.checked)}
                                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span className="text-sm text-gray-600">
                                            {t('strictCheck') || 'Strict check'}
                                        </span>
                                    </label>
                                </div>
                            )}
                        </div>

                        {/* Footer with Action Buttons */}
                        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                            <Button variant="secondary" size="lg" onClick={handleClose}>
                                {t('cancel') || 'Atšaukti'}
                            </Button>
                            <Button
                                variant="primary"
                                size="lg"
                                onClick={handleSubmit}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? <Loading /> : (t('save') || 'Išsaugoti')}
                            </Button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    )
}
